import { useEffect, useRef, useState, useCallback } from 'react'
import { useSearchParams } from 'react-router-dom'
import { Button } from '../components/ui/Button'

/**
 * DesktopViewer — Remote desktop viewer that connects to the relay WebSocket,
 * receives JPEG video frames from the desktop agent, and sends keyboard/mouse
 * input back through the relay.
 *
 * URL params:
 *   ws      — relay WebSocket URL
 *   session — session ID (display only)
 */
export default function DesktopViewer() {
  const [params] = useSearchParams()
  const wsUrl = params.get('ws') || ''
  const sessionId = params.get('session') || ''

  const canvasRef = useRef<HTMLCanvasElement>(null)
  const wsRef = useRef<WebSocket | null>(null)
  const [status, setStatus] = useState<'connecting' | 'connected' | 'streaming' | 'disconnected' | 'error'>('connecting')
  const [resolution, setResolution] = useState({ w: 0, h: 0 })
  const [fps, setFps] = useState(0)
  const [frameCount, setFrameCount] = useState(0)
  const [quality, setQuality] = useState('balanced')
  const [fullscreen, setFullscreen] = useState(false)
  const lastFrameTime = useRef(0)
  const fpsCounter = useRef(0)
  const fpsTimer = useRef<ReturnType<typeof setInterval>>(undefined)

  // FPS counter
  useEffect(() => {
    fpsTimer.current = setInterval(() => {
      setFps(fpsCounter.current)
      fpsCounter.current = 0
    }, 1000)
    return () => clearInterval(fpsTimer.current)
  }, [])

  // WebSocket connection
  useEffect(() => {
    if (!wsUrl) {
      setStatus('error')
      return
    }

    const ws = new WebSocket(wsUrl)
    wsRef.current = ws
    ws.binaryType = 'arraybuffer'

    ws.onopen = () => {
      setStatus('connected')
    }

    ws.onmessage = (event) => {
      if (event.data instanceof ArrayBuffer) {
        handleVideoFrame(new Uint8Array(event.data))
      } else if (typeof event.data === 'string') {
        handleControlMessage(event.data)
      }
    }

    ws.onclose = () => setStatus('disconnected')
    ws.onerror = () => setStatus('error')

    return () => { ws.close() }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wsUrl])

  const handleControlMessage = useCallback((data: string) => {
    try {
      const env = JSON.parse(data)
      if (env.type === 'desktop_connected') {
        const p = JSON.parse(env.payload || '{}')
        if (p.ok) {
          setStatus('streaming')
          setResolution({ w: p.width, h: p.height })
        } else {
          setStatus('error')
        }
      } else if (env.type === 'desktop_clipboard') {
        const p = JSON.parse(env.payload || '{}')
        if (p.text) {
          navigator.clipboard.writeText(p.text).catch(() => {})
        }
      }
    } catch { /* ignore non-JSON */ }
  }, [])

  const handleVideoFrame = useCallback((data: Uint8Array) => {
    // Binary frame format: [type:1][sessionId:4][length:4][payload:N]
    if (data.length < 9) return
    const frameType = data[0]
    // const sessionId = (data[1] << 24) | (data[2] << 16) | (data[3] << 8) | data[4]
    const payloadLen = (data[5] << 24) | (data[6] << 16) | (data[7] << 8) | data[8]
    const payload = data.slice(9, 9 + payloadLen)

    if (frameType === 0x10) { // FrameVideoJPEG
      renderJPEGFrame(payload)
    }
  }, [])

  const renderJPEGFrame = useCallback((jpegData: Uint8Array) => {
    const canvas = canvasRef.current
    if (!canvas) return

    const blob = new Blob([new Uint8Array(jpegData)], { type: 'image/jpeg' })
    const url = URL.createObjectURL(blob)
    const img = new Image()
    img.onload = () => {
      const ctx = canvas.getContext('2d')
      if (!ctx) return

      // Resize canvas to match frame
      if (canvas.width !== img.width || canvas.height !== img.height) {
        canvas.width = img.width
        canvas.height = img.height
        setResolution({ w: img.width, h: img.height })
      }

      ctx.drawImage(img, 0, 0)
      URL.revokeObjectURL(url)

      fpsCounter.current++
      setFrameCount(prev => prev + 1)
      lastFrameTime.current = Date.now()
    }
    img.src = url
  }, [])

  // Keyboard input
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (status !== 'streaming') return
      e.preventDefault()
      e.stopPropagation()

      const ws = wsRef.current
      if (!ws || ws.readyState !== WebSocket.OPEN) return

      const msg = {
        type: 'desktop_input',
        payload: JSON.stringify({
          session_id: 0, // filled by relay from session context
          type: e.type === 'keydown' ? 'keydown' : 'keyup',
          key: e.key,
          keycode: e.keyCode,
        }),
      }
      ws.send(JSON.stringify(msg))
    }

    window.addEventListener('keydown', handleKey, true)
    window.addEventListener('keyup', handleKey, true)
    return () => {
      window.removeEventListener('keydown', handleKey, true)
      window.removeEventListener('keyup', handleKey, true)
    }
  }, [status])

  // Mouse input
  const handleMouseEvent = useCallback((e: React.MouseEvent<HTMLCanvasElement>, type: string) => {
    if (status !== 'streaming') return
    const ws = wsRef.current
    if (!ws || ws.readyState !== WebSocket.OPEN) return

    const canvas = canvasRef.current
    if (!canvas) return

    const rect = canvas.getBoundingClientRect()
    const x = (e.clientX - rect.left) / rect.width
    const y = (e.clientY - rect.top) / rect.height

    const msg = {
      type: 'desktop_input',
      payload: JSON.stringify({
        session_id: 0,
        type,
        x, y,
        button: e.button,
      }),
    }
    ws.send(JSON.stringify(msg))
  }, [status])

  const handleScroll = useCallback((e: React.WheelEvent<HTMLCanvasElement>) => {
    if (status !== 'streaming') return
    const ws = wsRef.current
    if (!ws || ws.readyState !== WebSocket.OPEN) return
    e.preventDefault()

    const msg = {
      type: 'desktop_input',
      payload: JSON.stringify({
        session_id: 0,
        type: 'scroll',
        dy: e.deltaY,
        dx: e.deltaX,
      }),
    }
    ws.send(JSON.stringify(msg))
  }, [status])

  const sendCtrlAltDel = () => {
    const ws = wsRef.current
    if (!ws || ws.readyState !== WebSocket.OPEN) return
    // Send Ctrl, Alt, Delete as separate keydown events
    const keys = [
      { key: 'Control', keycode: 0x11, type: 'keydown' },
      { key: 'Alt', keycode: 0x12, type: 'keydown' },
      { key: 'Delete', keycode: 0x2E, type: 'keydown' },
      { key: 'Delete', keycode: 0x2E, type: 'keyup' },
      { key: 'Alt', keycode: 0x12, type: 'keyup' },
      { key: 'Control', keycode: 0x11, type: 'keyup' },
    ]
    for (const k of keys) {
      ws.send(JSON.stringify({ type: 'desktop_input', payload: JSON.stringify({ session_id: 0, ...k }) }))
    }
  }

  const changeQuality = (q: string) => {
    setQuality(q)
    const ws = wsRef.current
    if (!ws || ws.readyState !== WebSocket.OPEN) return
    ws.send(JSON.stringify({
      type: 'desktop_resize',
      payload: JSON.stringify({ session_id: 0, quality: q, max_fps: q === 'low' ? 15 : q === 'high' ? 60 : 30 }),
    }))
  }

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen()
      setFullscreen(true)
    } else {
      document.exitFullscreen()
      setFullscreen(false)
    }
  }

  const disconnect = () => {
    wsRef.current?.close()
    window.close()
  }

  const statusColors: Record<string, string> = {
    connecting: 'var(--accent)', connected: 'var(--accent)',
    streaming: 'var(--ok)', disconnected: 'var(--text-3)', error: 'var(--err)',
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100vh', background: '#000' }}>
      {/* Toolbar */}
      <div style={{
        height: 44, background: 'var(--bg-1)', borderBottom: '1px solid var(--border)',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '0 12px', flexShrink: 0, zIndex: 10,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ width: 8, height: 8, borderRadius: '50%', background: statusColors[status] }} />
          <span style={{ fontFamily: 'var(--mono)', fontSize: 12, color: 'var(--text-1)' }}>
            Remote Desktop
          </span>
          <span style={{
            fontSize: 11, color: 'var(--text-3)', padding: '2px 8px',
            borderRadius: 'var(--r-f)', background: 'var(--bg-2)', textTransform: 'capitalize',
          }}>
            {status}
          </span>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          {/* Quality selector */}
          <div style={{ display: 'flex', gap: 2 }}>
            {['low', 'balanced', 'high'].map(q => (
              <button
                key={q}
                onClick={() => changeQuality(q)}
                style={{
                  padding: '3px 8px', borderRadius: 'var(--r-f)', fontSize: 10, fontWeight: 600,
                  border: quality === q ? '1px solid var(--accent)' : '1px solid var(--border)',
                  background: quality === q ? 'var(--accent-m)' : 'var(--bg-2)',
                  color: quality === q ? 'var(--accent)' : 'var(--text-3)',
                  cursor: 'pointer', textTransform: 'capitalize',
                }}
              >
                {q}
              </button>
            ))}
          </div>

          <Button size="sm" variant="ghost" onClick={sendCtrlAltDel} title="Send Ctrl+Alt+Del">
            CAD
          </Button>
          <Button size="sm" variant="ghost" onClick={toggleFullscreen}>
            {fullscreen ? 'Exit FS' : 'Fullscreen'}
          </Button>
          <Button size="sm" variant="danger" onClick={disconnect}>
            Disconnect
          </Button>
        </div>
      </div>

      {/* Status bar */}
      <div style={{
        height: 24, background: 'var(--bg-2)', borderBottom: '1px solid var(--border)',
        display: 'flex', alignItems: 'center', padding: '0 12px', gap: 16,
        fontSize: 10, fontFamily: 'var(--mono)', color: 'var(--text-3)', flexShrink: 0,
      }}>
        {resolution.w > 0 && <span>{resolution.w}x{resolution.h}</span>}
        <span>{fps} FPS</span>
        <span>{frameCount} frames</span>
        <span>Session: {sessionId.slice(0, 8)}</span>
      </div>

      {/* Canvas area */}
      <div style={{
        flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
        overflow: 'hidden', position: 'relative',
      }}>
        {status === 'error' ? (
          <div style={{ textAlign: 'center', color: 'var(--text-2)' }}>
            <div style={{ fontSize: 14, marginBottom: 8 }}>Failed to connect</div>
            <Button variant="secondary" onClick={() => window.location.reload()}>Retry</Button>
          </div>
        ) : status === 'connecting' || status === 'connected' ? (
          <div style={{ textAlign: 'center', color: 'var(--text-3)' }}>
            <div style={{
              width: 32, height: 32, border: '3px solid var(--border)',
              borderTopColor: 'var(--accent)', borderRadius: '50%',
              animation: 'spin 1s linear infinite', margin: '0 auto 12px',
            }} />
            <div style={{ fontSize: 13 }}>Connecting to remote desktop...</div>
          </div>
        ) : (
          <canvas
            ref={canvasRef}
            style={{
              maxWidth: '100%', maxHeight: '100%',
              objectFit: 'contain', cursor: 'default',
              imageRendering: 'auto',
            }}
            onMouseMove={(e) => handleMouseEvent(e, 'mousemove')}
            onMouseDown={(e) => handleMouseEvent(e, 'mousedown')}
            onMouseUp={(e) => handleMouseEvent(e, 'mouseup')}
            onWheel={handleScroll}
            onContextMenu={(e) => e.preventDefault()}
            tabIndex={0}
          />
        )}
      </div>
    </div>
  )
}
